import { useTranslation } from 'react-i18next';
import { Typography, Box, Collapse } from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { DISPLAY_DATE_TIME_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import {
	LogHistoryDTO,
	LogHistoryFilter,
} from '@/api/DTO/pre-admission/preAdmission.interface';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useGetInterventionHistoryQuery } from '@/api/member-management/intervention';

interface Props {
	formId: string;
}

export default function LogHistory(props: Props) {
	const { formId } = props;
	const { t } = useTranslation();

	const [logInformationExpanded, setLogInformationExpanded] = useState(true);

	const defaultPropertyFilter: LogHistoryFilter = {
		pagination: initialPagination,
		searchString: '',
		formId: formId,
		sortings: [],
	};
	const defaultValues: DefaultValues<LogHistoryFilter> = defaultPropertyFilter;
	const methods = useForm<LogHistoryFilter>({
		defaultValues,
	});

	const {
		data: propertySummary,
		isSuccess: isGetPropertySummarySuccess,
		refetch: refetch,
	} = useGetInterventionHistoryQuery(methods.getValues(), {
		refetchOnMountOrArgChange: true,
	});

	const watchPage = methods.watch('pagination');

	useEffect(() => {
		refetch();
	}, [watchPage]);

	const transformResponse = useCallback((response?: LogHistoryDTO[]) => {
		if (response == null) return;
		const newResponse: any[] | undefined = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				createdAt: !item.createdAt
					? '-'
					: dayjs(item.createdAt).locale('en').format(DISPLAY_DATE_TIME_FORMAT),
			});
		});
		return newResponse;
	}, []);
	const columns: GridColDef[] = [
		{
			field: 'createdAt',
			headerName: t('preAdmission.modifiedDateTime'),
			flex: 15,
		},
		{
			field: 'actionTypeDesc',
			headerName: t('preAdmission.action'),
			flex: 15,
		},
		{
			field: 'actionedByDesc',
			headerName: t('preAdmission.actionedBy'),
			flex: 15,
		},
	];

	return (
		<>
			<Box
				display='flex'
				justifyContent='space-between'
				marginBottom={2}
				onClick={() => setLogInformationExpanded(!logInformationExpanded)}
			>
				<Typography variant='h6' sx={{ fontWeight: 'bold' }}>
					{t('preAdmission.logHistory')}
				</Typography>
				<ExpandMoreIcon
					sx={{
						transform: logInformationExpanded ? 'rotate(180deg)' : 'none',
					}}
				/>
			</Box>
			<Collapse in={logInformationExpanded}>
				<FormProvider {...methods}>
					<Box marginTop={2}>
						<AppDataGrid
							loading={!isGetPropertySummarySuccess}
							rows={transformResponse(propertySummary?.data?.items) ?? []}
							rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
							columns={columns}
							getRowId={(row) => row.id}
						/>
					</Box>
				</FormProvider>
			</Collapse>
		</>
	);
}
